import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react'
import jwtDecode, { JwtPayload } from 'jwt-decode'
import log from 'loglevel'
import { Metadata } from 'grpc-web'
import {
  USE_CASE,
  USER_ROLE,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { getEnumKeyName } from 'src/Utils/helpers/string.helpers'
import { AuthorizationPromiseClient } from '@trustero/trustero-api-web/lib/authz/authz_grpc_web_pb'
import {
  CAPABILITY,
  PERMISSION_RULE,
} from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { TOUR_LS_PREFIX } from 'src/hooks/useTour.constants'
import { loadFromStorage, saveToStorage } from '../Utils/storage'
import {
  clearImpersonationSession,
  readImpersonationSession,
  writeImpersonationSession,
} from '../Utils/impersonation.helpers'
import { authorizedGrpcClientFactory } from '../adapter'

const STORAGE_KEY = 'authcontext'
const logger = log.getLogger('context')

const PRESERVED_LS_PREFIXES = [TOUR_LS_PREFIX]

function clearLocalStorage(): void {
  clearImpersonationSession()
  const preserved: [string, string][] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && PRESERVED_LS_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      const value = localStorage.getItem(key)
      if (value !== null) preserved.push([key, value])
    }
  }
  localStorage.clear()
  preserved.forEach(([key, value]) => localStorage.setItem(key, value))
}

export type PermissionInfo = {
  rule: PERMISSION_RULE
  capabilityName: string
  ruleName: string
}

export type Permissions = {
  version: number
  capabilities: Partial<Record<CAPABILITY, PermissionInfo>>
  readOnlyLabelIds?: string[]
  contentRestrictedLabelIds?: string[]
  noAccessLabelIds?: string[]
  fullAccessLabelIds?: string[]
}

export type ImpersonationInfo = {
  isImpersonating: boolean
  originalRole: USER_ROLE
  originalPermissions?: Permissions
  accessRoleId?: string
  accessRoleName?: string
  groupIds?: string[]
  groupNames?: string[]
  allowedRoutes?: string[]
  homeRoute?: string
}

export type JwtNtrcePayload = JwtPayload & {
  aid: string
  email: string
  auc: string
  rol: string
}

export type AuthRecord = {
  isAuthenticated: boolean
  accessToken: string
  accountId: string
  email: string
  useCase: USE_CASE
  role: USER_ROLE
  isAuthLoading: boolean
  permissions?: Permissions
  isVendorOnlyAccount: boolean
  impersonation?: ImpersonationInfo
}

export type AuthAction = {
  type: AuthActionType
  accessToken?: string
  accountId?: string
  email?: string
  useCase?: USE_CASE
  role?: USER_ROLE
  permissions?: Permissions
  logoutReason?: string
  isVendorOnlyAccount?: boolean
  impersonation?: ImpersonationInfo
}

export const empty_authctx: AuthRecord = {
  isAuthenticated: false,
  accessToken: '',
  accountId: '',
  email: '',
  useCase: USE_CASE.SAAS_BUYER, // we want to default to limited access
  role: USER_ROLE.READONLY, // we want to default to limited access
  isAuthLoading: true, // we want to default to loading
  isVendorOnlyAccount: false,
}

export enum AuthActionType {
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  REGISTER_LOGIN = 'REGISTER_LOGIN',
  SET_EMAIL = 'SET_EMAIL',
  REFRESH_TOKEN = 'REFRESH_TOKEN',
  QUIET_LOGOUT = 'QUIET_LOGOUT',
  LOAD_PERMISSIONS = 'LOAD_PERMISSIONS',
  START_IMPERSONATION = 'START_IMPERSONATION',
  STOP_IMPERSONATION = 'STOP_IMPERSONATION',
}

export const tokenDecodeToMinutes = (
  accessToken: string,
  debug: boolean,
): { age: number; expiration: number } => {
  const result = {
    age: Number.MAX_SAFE_INTEGER,
    expiration: -1,
  }
  if (!accessToken) {
    return result
  }
  const nowUTC = Date.now() / 1000.0
  const {
    exp: expirationUTC,
    aid: accountId,
    iat: issuedUTC,
    auc: useCase,
    rol: role,
  } = jwtDecode<JwtNtrcePayload>(accessToken)

  if (expirationUTC) {
    const deltaMinutes = (expirationUTC - nowUTC) / 60
    result.expiration = Math.floor(deltaMinutes)
  }
  if (issuedUTC) {
    const deltaMinutes = (nowUTC - issuedUTC) / 60
    result.age = Math.floor(deltaMinutes)
  }
  if (debug) {
    logger.debug('token expiration =', expirationUTC, 'now = ', nowUTC)
    logger.debug('  ', result.expiration, 'minutes until expiration')
    logger.debug('  ', result.age, 'minutes since created')
    logger.debug('  accountId ', accountId)
    logger.debug('  useCase', useCase)
    logger.debug('  role', role)
  }
  return result
}

const _load = (): AuthRecord => {
  // default empty auth info
  const authctx = loadFromStorage(STORAGE_KEY, empty_authctx)
  const impersonation = readImpersonationSession()
  if (impersonation) {
    authctx.impersonation = impersonation
  }
  logger.debug('loaded authctx for', authctx.email)
  //to print out debug information about accessToken time
  tokenDecodeToMinutes(authctx.accessToken, true)
  return authctx
}
const initCtxOnLoad = _load()

const _save = (authctx: AuthRecord) => {
  const toPersist = authctx.impersonation?.isImpersonating
    ? {
        ...authctx,
        role: authctx.impersonation.originalRole,
        permissions:
          authctx.impersonation.originalPermissions ?? authctx.permissions,
        impersonation: undefined,
      }
    : { ...authctx, impersonation: undefined }

  saveToStorage(STORAGE_KEY, toPersist)
  logger.debug('saved authctx for', authctx.email)
  //to print out debug information about accessToken time
  tokenDecodeToMinutes(authctx.accessToken, true)
}

export const useAuth = (): AuthRecord => {
  const { authCtx } = useContext(AuthContext)
  return authCtx
}

export const useToken = (): string => {
  const auth = useAuth()
  return auth.accessToken
}

// TODO: Remove read only from provider and add a new hook
export const useIsProvider = (): boolean => {
  const { authCtx } = useContext(AuthContext)
  return authCtx.useCase === USE_CASE.SAAS_PROVIDER
}

// deniedInternalEmails is a hardcoded denylist of emails that must never be
// treated as internal Trustero users, even though they're on the
// @trustero.com domain (e.g. a dedicated reviewer/test account). Keep this in
// sync with the backend denylist in ntrced/auth/auth.go.
const deniedInternalEmails = [
  'trustero.reviewer@trustero.com',
  'trustero.reviewer.1@trustero.com',
  'trustero.reviewer.2@trustero.com',
]

export const isDeniedInternalEmail = (email: string): boolean =>
  deniedInternalEmails.includes(email.toLowerCase())

export const useIsInternalUser = (): boolean => {
  const { authCtx } = useContext(AuthContext)
  return (
    authCtx.email.toLowerCase().endsWith('@trustero.com') &&
    !isDeniedInternalEmail(authCtx.email)
  )
}

export const useIsReadOnlyUser = (): boolean => {
  const { authCtx } = useContext(AuthContext)
  return authCtx.role === USER_ROLE.READONLY
}

export const useIsAdmin = (): boolean => {
  const { authCtx } = useContext(AuthContext)
  return authCtx.role === USER_ROLE.ADMIN
}

export const useIsUser = (): boolean => {
  const { authCtx } = useContext(AuthContext)
  return authCtx.role === USER_ROLE.USER
}

export const useIsTrustPortalPublisher = (): boolean => {
  const { authCtx } = useContext(AuthContext)
  return authCtx.role === USER_ROLE.TRUSTPORTAL_PUBLISHER
}

export const useIsTrustPortalApprover = (): boolean => {
  const { authCtx } = useContext(AuthContext)
  return authCtx.role === USER_ROLE.TRUSTPORTAL_APPROVER
}

export const useIsAuditor = (): boolean => {
  const { authCtx } = useContext(AuthContext)
  return authCtx.role === USER_ROLE.AUDITOR
}

export const useIsBuyer = (): boolean => {
  const { authCtx } = useContext(AuthContext)
  return authCtx.useCase === USE_CASE.SAAS_BUYER
}

export const useIsVendorOnlyAccount = (): boolean => {
  const { authCtx } = useContext(AuthContext)
  return authCtx.isVendorOnlyAccount
}

export const useCurrentUserEmail = (): string => {
  const { authCtx } = useContext(AuthContext)
  return authCtx.email
}

type AuthContextType = {
  authCtx: AuthRecord
  authDispatch: React.Dispatch<AuthAction>
}

export const AuthContext = createContext<AuthContextType>({
  authCtx: initCtxOnLoad,
  authDispatch: () => null,
})

const logMissingParams = (
  message: string,
  params: Array<{ name: string; exists: boolean }>,
) => {
  let errorMessage = `${message}:`
  let count = 0
  params.forEach((param) => {
    if (!param.exists) {
      errorMessage =
        count === 0
          ? `${errorMessage} ${param.name}`
          : `${errorMessage} and ${param.name}`
      count++
    }
  })
  errorMessage = `${errorMessage} not provided`
  if (count) log.error('Error in authContext', errorMessage)
}

const isEqual = (x: AuthRecord, y: AuthRecord): boolean => {
  // Compare permissions by serializing them - handles undefined/null cases
  const xPerms = x.permissions ? JSON.stringify(x.permissions) : null
  const yPerms = y.permissions ? JSON.stringify(y.permissions) : null
  const xImp = x.impersonation ? JSON.stringify(x.impersonation) : null
  const yImp = y.impersonation ? JSON.stringify(y.impersonation) : null

  return (
    x.isAuthenticated === y.isAuthenticated &&
    x.accessToken === y.accessToken &&
    x.accountId === y.accountId &&
    x.email === y.email &&
    x.useCase === y.useCase &&
    x.isAuthLoading === y.isAuthLoading &&
    x.role === y.role &&
    x.isVendorOnlyAccount === y.isVendorOnlyAccount &&
    xPerms === yPerms &&
    xImp === yImp
  )
}

const updatedState = (
  original: AuthRecord,
  updated: AuthRecord,
): AuthRecord => {
  return isEqual(original, updated) ? original : updated
}

export const authReducer = (
  state: AuthRecord,
  action: AuthAction,
): AuthRecord => {
  let newState = empty_authctx
  const {
    type,
    accessToken,
    accountId,
    email,
    useCase,
    role,
    permissions,
    isVendorOnlyAccount,
    impersonation,
  } = action

  switch (type) {
    case AuthActionType.LOGIN: {
      if (
        accessToken &&
        accountId !== undefined &&
        email &&
        useCase !== undefined &&
        role !== undefined
      ) {
        newState = {
          ...state,
          isAuthenticated: true,
          accessToken,
          accountId,
          email,
          useCase,
          role,
          isAuthLoading: true, // Set to true so permissions can be loaded
          isVendorOnlyAccount: false,
        }
        _save(newState)
        return updatedState(state, newState)
      }

      logMissingParams('Failed to login', [
        { name: 'access token', exists: Boolean(accessToken) },
        { name: 'account id', exists: Boolean(accountId) },
        { name: 'email', exists: Boolean(email) },
      ])
      return state
    }
    case AuthActionType.LOGOUT: {
      clearLocalStorage()
      // save the email address so that we can prefill the email-a-link widget
      if (state?.email) {
        newState.email = state.email
      }
      _save(newState)
      const reason = action.logoutReason || 'unspecified'
      // eslint-disable-next-line no-console
      console.info(`[auth] Redirecting to /login (reason: ${reason})`)
      // Ensure the window change waits for call
      setTimeout(() => (window.location.href = '/login')) // nosemgrep
      return updatedState(state, _load())
    }
    // log the user out without redirecting
    // currently only used on the signup page
    case AuthActionType.QUIET_LOGOUT: {
      clearLocalStorage()

      _save(newState)
      return updatedState(state, _load())
    }
    case AuthActionType.REGISTER_LOGIN: {
      if (
        accessToken &&
        accountId &&
        email &&
        useCase !== undefined &&
        role !== undefined
      ) {
        newState = {
          ...state,
          accessToken,
          isAuthenticated: true,
          accountId,
          email,
          useCase,
          role,
          isAuthLoading: true, // Set to true so permissions can be loaded
          isVendorOnlyAccount: false,
        }
        _save(newState)
        return updatedState(state, newState)
      }

      logMissingParams('Failed to register user and login', [
        { name: 'account id', exists: Boolean(accountId) },
        { name: 'email', exists: Boolean(email) },
        { name: 'useCase', exists: useCase !== undefined },
        { name: 'role', exists: role !== undefined },
      ])
      return state
    }
    case AuthActionType.SET_EMAIL: {
      if (email) {
        newState = {
          ...state,
          isAuthenticated: false,
          email,
        }
        _save(newState)
        return updatedState(state, newState)
      }

      logMissingParams('Failed to set email', [
        { name: 'email', exists: Boolean(email) },
      ])
      return state
    }
    case AuthActionType.REFRESH_TOKEN: {
      if (
        accessToken &&
        accountId &&
        useCase !== undefined &&
        role !== undefined
      ) {
        // A refresh carries the authentic role. While previewing another role
        // that has to land on originalRole, or the preview would silently fall
        // back to the real (admin) role while still showing preview
        // permissions and the preview banner.
        const isImpersonating = Boolean(state.impersonation?.isImpersonating)
        const refreshedImpersonation = state.impersonation && {
          ...state.impersonation,
          originalRole: role,
        }
        newState = {
          ...state,
          isAuthenticated: true,
          accessToken,
          accountId,
          useCase,
          role: isImpersonating ? state.role : role,
          impersonation: refreshedImpersonation,
          isAuthLoading: true, // Set to true so permissions can be reloaded
          isVendorOnlyAccount: false,
        }
        if (refreshedImpersonation) {
          writeImpersonationSession(refreshedImpersonation)
        }
        _save(newState)
        return updatedState(state, newState)
      }
      logMissingParams('Failed to refresh token', [
        { name: 'access token', exists: Boolean(accessToken) },
        { name: 'account id', exists: Boolean(accountId) },
        { name: 'useCase', exists: useCase !== undefined },
        { name: 'role', exists: role !== undefined },
      ])
      return state
    }
    case AuthActionType.LOAD_PERMISSIONS: {
      if (accessToken && accountId && useCase !== undefined) {
        if (state.impersonation?.isImpersonating) {
          newState = {
            ...state,
            impersonation: {
              ...state.impersonation,
              originalPermissions: permissions,
            },
            isVendorOnlyAccount: Boolean(isVendorOnlyAccount),
            isAuthLoading: false,
          }
          _save(newState)
          return updatedState(state, newState)
        }
        // auth is loaded, permissions may still be undefined
        // this means the account has not been migrated to the new auth system and is okay
        newState = {
          ...state,
          permissions,
          isVendorOnlyAccount: Boolean(isVendorOnlyAccount),
          isAuthLoading: false,
        }
        _save(newState)
        return updatedState(state, newState)
      }
      logMissingParams('Failed to load permissions', [
        { name: 'access token', exists: Boolean(accessToken) },
        { name: 'account id', exists: Boolean(accountId) },
        { name: 'useCase', exists: useCase !== undefined },
      ])
      return state
    }
    case AuthActionType.START_IMPERSONATION: {
      const authenticRole = state.impersonation?.isImpersonating
        ? state.impersonation.originalRole
        : state.role

      if (authenticRole !== USER_ROLE.ADMIN) {
        logger.error('Unauthorized impersonation attempt by non-admin user')
        return state
      }

      if (impersonation) {
        newState = {
          ...state,
          role: role ?? state.role,
          permissions: permissions ?? state.permissions,
          impersonation,
        }
        writeImpersonationSession(impersonation)
        return updatedState(state, newState)
      }
      return state
    }
    case AuthActionType.STOP_IMPERSONATION: {
      if (!state.impersonation?.isImpersonating) {
        return state
      }
      newState = {
        ...state,
        role: state.impersonation.originalRole,
        permissions: state.impersonation.originalPermissions,
        impersonation: undefined,
      }
      clearImpersonationSession()
      return updatedState(state, newState)
    }
    default:
      throw new Error(`Unknown action type [${action.type}]`)
  }
}

export const AuthProvider = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element => {
  const [authCtx, authDispatch] = useReducer(authReducer, initCtxOnLoad)

  useEffect(() => {
    // Only run when we have a token and are in loading state
    if (!authCtx.accessToken || !authCtx.isAuthLoading) {
      return
    }
    // Capture current values to avoid stale closures in async function
    const currentAccessToken = authCtx.accessToken
    const currentAccountId = authCtx.accountId
    const currentUseCase = authCtx.useCase
    const currentRole = authCtx.role
    // Set when a newer authCtx supersedes this effect, so a stale
    // getPermissions response can't clobber the new account's permissions
    let cancelled = false
    let retryTimeout: ReturnType<typeof setTimeout> | undefined

    async function loadPermissions(attempt = 0) {
      try {
        // Create client directly to avoid circular dependency with useAuthorizedGrpcClient
        // which uses AuthContext that this component provides
        const authorizationClient: AuthorizationPromiseClient =
          authorizedGrpcClientFactory(
            AuthorizationPromiseClient,
            authCtx,
            authDispatch,
          )
        const response = await authorizationClient.getPermissions(new Empty())
        if (!response || cancelled) {
          return
        }

        // permissions may be undefined/null - this is okay for accounts not migrated to new auth system
        const permissions = response.getPermissionsList()
        const version = response.getVersion()
        const permissionsMap = permissions.reduce((acc, permission) => {
          const capability = permission.getCapability()
          const rule = permission.getRule()
          acc[capability] = {
            rule,
            capabilityName: getEnumKeyName(CAPABILITY, capability),
            ruleName: getEnumKeyName(PERMISSION_RULE, rule),
          } as PermissionInfo
          return acc
        }, {} as Record<CAPABILITY, PermissionInfo>)

        authDispatch({
          type: AuthActionType.LOAD_PERMISSIONS,
          accessToken: currentAccessToken,
          accountId: currentAccountId,
          useCase: currentUseCase,
          role: currentRole,
          permissions: {
            version,
            capabilities: permissionsMap,
            readOnlyLabelIds: response.getReadOnlyLabelIdsList(),
            contentRestrictedLabelIds:
              response.getContentRestrictedLabelIdsList(),
            noAccessLabelIds: response.getNoAccessLabelIdsList(),
            fullAccessLabelIds: response.getFullAccessLabelIdsList(),
          },
          isVendorOnlyAccount: response.getIsVendorOnlyAccount(),
        })
      } catch (err) {
        logger.error('Failed to load permissions:', err)
        if (cancelled) return
        // Real auth failures are handled inside the gRPC client (it dispatches
        // LOGOUT and resolves undefined), so errors here are transport-level —
        // e.g. browser aborts in-flight XHRs on full-page navigation. Logging
        // out on those wiped sessions mid account-switch; retry instead.
        if (attempt < 2) {
          retryTimeout = setTimeout(
            () => loadPermissions(attempt + 1),
            1000 * (attempt + 1),
          )
          return
        }
        // Out of retries: end the loading phase without logging out. Undefined
        // permissions is a supported state (role-based fallback), so the app
        // renders instead of hanging on a spinner; a later token refresh retries.
        authDispatch({
          type: AuthActionType.LOAD_PERMISSIONS,
          accessToken: currentAccessToken,
          accountId: currentAccountId,
          useCase: currentUseCase,
          role: currentRole,
        })
      }
    }

    loadPermissions()
    return () => {
      cancelled = true
      clearTimeout(retryTimeout)
    }
  }, [authCtx, authDispatch])

  return (
    <AuthContext.Provider value={{ authCtx, authDispatch }}>
      {children}
    </AuthContext.Provider>
  )
}

const authHeader = (accessToken: string): Metadata => {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
}

export const useAuthHeader = (): Metadata => {
  const { authCtx } = useContext(AuthContext)
  return authHeader(authCtx.accessToken)
}

export const useAuthContext = (): AuthContextType => useContext(AuthContext)

export const useHandleSignupLogout = (): (() => void) => {
  const { authCtx } = useAuthContext()

  return useCallback(() => {
    authReducer(authCtx, {
      type: AuthActionType.QUIET_LOGOUT,
      useCase: USE_CASE.SAAS_BUYER,
      role: USER_ROLE.READONLY,
    })
  }, [authCtx])
}

export const useImpersonation = (): {
  isImpersonating: boolean
  impersonation?: ImpersonationInfo
  startImpersonation: (
    info: ImpersonationInfo,
    impersonatedRole: USER_ROLE,
    impersonatedPermissions: Permissions,
  ) => void
  stopImpersonation: () => void
} => {
  const { authCtx, authDispatch } = useAuthContext()
  const isImpersonating = Boolean(authCtx.impersonation?.isImpersonating)

  const startImpersonation = useCallback(
    (
      info: ImpersonationInfo,
      impersonatedRole: USER_ROLE,
      impersonatedPermissions: Permissions,
    ) => {
      authDispatch({
        type: AuthActionType.START_IMPERSONATION,
        role: impersonatedRole,
        permissions: impersonatedPermissions,
        impersonation: info,
      })
    },
    [authDispatch],
  )

  const stopImpersonation = useCallback(() => {
    authDispatch({
      type: AuthActionType.STOP_IMPERSONATION,
    })
  }, [authDispatch])

  return {
    isImpersonating,
    impersonation: authCtx.impersonation,
    startImpersonation,
    stopImpersonation,
  }
}

export default AuthContext
